// @owners { team: patients }
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Column,
  Description,
  InlineAlert,
  InputPassword,
  LgPadding,
} from '@alto/design-system';
import React, { useContext } from 'react';
import { useController, useForm } from 'react-hook-form';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { mapControllerToInputProps } from '~shared/helpers/mapFieldToInputProps';
import { type APIError } from '~shared/types/APIError';

type Values = {
  password: string;
};
type Props = {
  readonly onSubmit: (values: Values) => Promise<void>;
  readonly error: APIError | null | undefined;
};

export const ReauthActionSheet = ({ onSubmit, error }: Props) => {
  const { closeActionSheet } = useContext(ActionSheetContext);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { password: '' } });
  const passwordController = useController({
    control,
    name: 'password',
    rules: { required: 'Password cannot be blank' },
  });

  const handleConfirmPassword = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <ActionSheetV2
      title="Confirm password"
      description="To edit this information please enter your password"
      analyticsName="reauth"
      buttons={[
        <Button
          key="submit-button"
          label="Submit"
          accessibilityLabel="Press to confirm password"
          loading={isSubmitting}
          loadingLabel="Submitting"
          onPress={handleConfirmPassword}
        />,
        <Button
          key="cancel-button"
          label="Cancel"
          accessibilityLabel="Press to cancel"
          onPress={closeActionSheet}
          type="tertiary"
        />,
      ]}
    >
      <LgPadding>
        <Column gap="MD">
          <InputPassword
            label="Password"
            placeholder="Enter password"
            onChangeText={passwordController.field.onChange}
            {...mapControllerToInputProps(passwordController)}
          />
          {error ? (
            <InlineAlert type="error">
              <Description>{getErrorMessageFromResponse({ error })}</Description>
            </InlineAlert>
          ) : null}
        </Column>
      </LgPadding>
    </ActionSheetV2>
  );
};
